"use client";

import { NativeLink } from "@/components/native-link";
import {
  Bot, BrainCircuit, CircleAlert, ClipboardCheck, ClipboardList,
  Database, Gauge, Headphones, LifeBuoy, LogOut, RadioTower, Settings,
  Bell, CircleUser, Users,
} from "lucide-react";
import { ReactNode, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { Select } from "@/components/ui/select";
import { adminSignOut } from "@/lib/auth";
import { useAuthProfile, useRoleImpersonation } from "@/lib/auth-context";
import { initialsFromName } from "@/lib/users-format";
import { hasPermission } from "@/lib/users-rbac";
import { useNavigate } from "react-router-dom";

type NavDef = {
  label: string;
  href: string;
  icon: typeof Gauge;
  permission: string;
  badge?: string;
};

const primaryBase: NavDef[] = [
  { label: "Dashboard", href: "/overview", icon: Gauge, permission: "dashboard.view" },
  { label: "Sessions", href: "/sessions", icon: RadioTower, permission: "sessions.view" },
  { label: "Agents", href: "/agents", icon: Bot, permission: "agents.view" },
  { label: "Perception", href: "/perception", icon: BrainCircuit, permission: "perception.view" },
  { label: "Recordings", href: "/recordings", icon: Headphones, permission: "recordings.view" },
  { label: "Users", href: "/users", icon: Users, permission: "users.view" },
  { label: "Settings", href: "/settings", icon: Settings, permission: "settings.view" },
];

const operationsBase: NavDef[] = [
  { label: "Checkups", href: "/checkups", icon: ClipboardCheck, permission: "checkups.view" },
  { label: "Incidents", href: "/incidents", icon: CircleAlert, permission: "incidents.view" },
  { label: "Audit log", href: "/audit", icon: ClipboardList, permission: "audit.view" },
];

const titles: Record<string, string> = {
  overview: "Dashboard", sessions: "Session management", agents: "Agent management",
  perception: "Perception intelligence", recordings: "Recording governance",
  users: "User management", settings: "Workspace settings", account: "User settings",
  checkups: "Checkups", incidents: "Incident operations", audit: "Audit evidence",
};

function subscribeToPath(onStoreChange: () => void) {
  window.addEventListener("popstate", onStoreChange);
  return () => window.removeEventListener("popstate", onStoreChange);
}

function getPath() {
  return window.location.pathname;
}

function getServerPath() {
  return "";
}

export function PulseLogo({ dark = true }: { dark?: boolean }) {
  return <div className="brand" style={dark ? undefined : { color: "#10233f" }} aria-label="Igentify Pulse">
    <span className="brand-mark" aria-hidden="true">{Array.from({ length: 7 }).map((_, i) => <i key={i} />)}</span>
    <span><span className="brand-name">Igentify Pulse</span></span>
  </div>;
}

function NavItem({
  item,
  path,
}: {
  item: { label: string; href: string; icon: typeof Gauge; badge?: string };
  path: string;
}) {
  const active = ["/sessions", "/checkups", "/incidents", "/audit", "/users"].includes(item.href)
    ? path.startsWith(item.href)
    : path.startsWith(item.href);
  const Icon = item.icon;
  return <NativeLink href={item.href} className={`nav-item ${active ? "active" : ""}`}>
    <Icon /><span>{item.label}</span>{item.badge && <span className="nav-badge">{item.badge}</span>}
  </NativeLink>;
}

function UserChip() {
  const profile = useAuthProfile();
  const path = useSyncExternalStore(subscribeToPath, getPath, getServerPath);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const first = profile?.firstName || "";
  const last = profile?.lastName || "";
  const email = profile?.email || "";
  const roleName = profile?.roleName || "Member";
  const short =
    first && last
      ? `${first} ${last.charAt(0)}.`
      : first || last || email.split("@")[0] || "User";
  const initials = initialsFromName(first, last, email);
  const onAccount = path.startsWith("/account");
  const canAccount = hasPermission(profile?.permissions, "account.view");

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function logOut() {
    setOpen(false);
    try {
      await adminSignOut();
    } finally {
      window.location.assign("/login");
    }
  }

  return (
    <div className="user-chip-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`user-chip ${open || onAccount ? "open" : ""}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="avatar">{initials}</span>
        <span className="user-meta">
          <b>{short}</b>
          <span>{roleName}</span>
        </span>
      </button>
      {open ? (
        <div className="user-chip-menu" role="menu">
          {canAccount ? (
            <NativeLink
              href="/account"
              role="menuitem"
              className="user-chip-menu-item"
              onClick={() => setOpen(false)}
            >
              <CircleUser size={16} />
              User settings
            </NativeLink>
          ) : null}
          <button type="button" role="menuitem" className="user-chip-menu-item" onClick={() => void logOut()}>
            <LogOut size={16} />
            Log out
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** First Workspace/Operations destination the actor may open. */
export function defaultAdminPath(permissions: string[] | undefined): string {
  const order = [...primaryBase, ...operationsBase];
  for (const item of order) {
    if (hasPermission(permissions, item.permission)) return item.href;
  }
  return "/sessions";
}

export function PulseShell({ children }: { children: ReactNode }) {
  const path = useSyncExternalStore(subscribeToPath, getPath, getServerPath);
  const navigate = useNavigate();
  const [env, setEnv] = useState("Production");
  const section = path.split("/")[1] || "overview";
  const profile = useAuthProfile();
  const { impersonation, exitImpersonation, realProfile } = useRoleImpersonation();
  const perms = profile?.permissions;
  const primary = primaryBase.filter((item) => hasPermission(perms, item.permission));
  const operations = operationsBase.filter((item) => hasPermission(perms, item.permission));
  const showSupport = hasPermission(perms, "ui.support");
  const showEnv = hasPermission(perms, "ui.env_switcher");
  const showBell = hasPermission(perms, "ui.notifications");

  // Role preview must not edit RBAC — leave Users while impersonating.
  useEffect(() => {
    if (!impersonation) return;
    if (!path.startsWith("/users")) return;
    navigate(defaultAdminPath(perms), { replace: true });
  }, [impersonation, path, navigate, perms]);

  function onExitImpersonation() {
    exitImpersonation?.();
    navigate(defaultAdminPath(realProfile?.permissions), { replace: true });
  }

  return <div className="pulse-shell">
    <aside className="sidebar">
      <PulseLogo />
      {primary.length > 0 ? (
        <>
          <div className="nav-label">Workspace</div>
          <nav className="nav-list" aria-label="Workspace navigation">{primary.map((item) => <NavItem key={item.href} item={item} path={path} />)}</nav>
        </>
      ) : null}
      {operations.length > 0 ? (
        <>
          <div className="nav-label">Operations</div>
          <nav className="nav-list" aria-label="Operations navigation">{operations.map((item) => <NavItem key={item.href} item={item} path={path} />)}</nav>
        </>
      ) : null}
      <div className="side-bottom">
        {showSupport ? (
          <div className="side-support"><LifeBuoy size={18} /><span><b>Need help?</b>Operations support</span></div>
        ) : null}
        <UserChip />
      </div>
    </aside>
    <section className="app-area">
      <header className="topbar">
        <div className="top-context"><span className="context-title">{titles[section] ?? "PULSE"}</span><span>•</span><span className="status-dot" /><span>All systems operational</span></div>
        <div className="top-actions">
          {showEnv ? (
            <Select
              size="sm"
              variant="toolbar"
              searchable={false}
              ariaLabel="Environment"
              className="topbar-env-select"
              value={env}
              onChange={(v) => setEnv(v ?? "Production")}
              options={["Production", "Staging", "Development"].map((x) => ({
                value: x,
                label: x,
                icon: <Database size={14} />,
              }))}
            />
          ) : null}
          {showBell ? (
            <button className="icon-button notification" aria-label="Notifications"><Bell /></button>
          ) : null}
        </div>
      </header>
      {impersonation ? (
        <div className="impersonation-banner" role="status">
          <span>
            Viewing as <b>{impersonation.roleName}</b>
            <span className="impersonation-banner-note">
              {" "}· Preview only — API writes still use your Administrator account
            </span>
          </span>
          <button type="button" className="btn small" onClick={onExitImpersonation}>
            Exit impersonation
          </button>
        </div>
      ) : null}
      <main className="content">{children}</main>
    </section>
  </div>;
}

export function PageHeader({ eyebrow, title, subtitle, actions }: { eyebrow?: ReactNode; title: string; subtitle: string; actions?: ReactNode }) {
  return <div className="page-head"><div>{eyebrow && <div className="eyebrow">{eyebrow}</div>}<h1 className="page-title">{title}</h1><p className="page-subtitle">{subtitle}</p></div>{actions && <div className="head-actions">{actions}</div>}</div>;
}

export function MetricCard({ label, value, foot, icon, tone = "blue" }: { label: string; value: string; foot: ReactNode; icon: ReactNode; tone?: "blue" | "green" | "orange" | "purple" | "red" }) {
  const colors = { blue: ["#edf5ff", "#0968f2"], green: ["#eaf9f3", "#0aa878"], orange: ["#fff5e7", "#e8960d"], purple: ["#f2edff", "#8057e8"], red: ["#fff0f1", "#e14d5a"] }[tone];
  return <div className="metric"><div className="metric-top"><span>{label}</span><span className="metric-icon" style={{ background: colors[0], color: colors[1] }}>{icon}</span></div><div className="metric-value">{value}</div><div className="metric-foot">{foot}</div></div>;
}

export function Pill({ children, tone = "gray", className = "" }: { children: ReactNode; tone?: "blue" | "green" | "red" | "orange" | "purple" | "gray"; className?: string }) {
  return <span className={`pill ${tone} ${className}`}>{children}</span>;
}

export function Card({ title, kicker, action, children, className = "" }: { title?: string; kicker?: string; action?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`card ${className}`}>
      {title ? (
        <div className="card-head">
          <div>
            <h2 className="card-title">{title}</h2>
            {kicker ? <span className="card-kicker">{kicker}</span> : null}
          </div>
          {action}
        </div>
      ) : null}
      <div className="card-body">{children}</div>
    </section>
  );
}
